/**
 * O estado de um lote no cockpit: o resumo, as imagens e o que cada IA leu.
 *
 * **O polling só existe enquanto há imagem na fila.** Ele é a única parte da tela que
 * consulta o servidor sozinha, e a condição de parada é o que o impede de virar carga
 * permanente: assim que `na_fila` chega a zero, o relógio desliga. Um `setInterval` fixo
 * ficaria batendo na API pelo resto do dia para descobrir que nada mudou.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  ImagemDoLote,
  ResultadoDaCapacidade,
  ResultadoDeImagem,
  ResumoDoLote,
} from "../api/tipos";

// 2,5 s: é o passo da barra "Processando o lote… N%". Só roda com imagem na fila.
export const INTERVALO_DO_POLLING_MS = 2500;

export interface EstadoDoLote {
  lote: ResumoDoLote | null;
  imagens: ImagemDoLote[];
  /** Resultados por imagem: o que cada capacidade disse sobre cada arquivo. */
  resultados: Record<string, ResultadoDaCapacidade[]>;
  carregando: boolean;
  erro: ErroDaApi | null;
  recarregar: () => Promise<void>;
}

/**
 * A rota do lote devolve uma lista plana — um resultado por (imagem, capacidade) — e não um
 * agrupamento. Agrupar é trabalho do cliente de propósito: assim a API não precisa decidir
 * como a tela quer ler, e uma tela nova que queira ordenar por capacidade não pede rota nova.
 */
function agrupar(
  linhas: ResultadoDeImagem[],
): Record<string, ResultadoDaCapacidade[]> {
  const porImagem: Record<string, ResultadoDaCapacidade[]> = {};
  for (const linha of linhas) {
    const { imagem_id, ...resultado } = linha;
    (porImagem[imagem_id] ??= []).push(resultado);
  }
  return porImagem;
}

export function useLote(loteId: string | null): EstadoDoLote {
  const [lote, definirLote] = useState<ResumoDoLote | null>(null);
  const [imagens, definirImagens] = useState<ImagemDoLote[]>([]);
  const [resultados, definirResultados] = useState<
    Record<string, ResultadoDaCapacidade[]>
  >({});
  const [carregando, definirCarregando] = useState(false);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buscar = useCallback(async () => {
    if (!loteId) {
      definirLote(null);
      definirImagens([]);
      definirResultados({});
      definirErro(null);
      return;
    }
    definirCarregando(true);
    try {
      const [resumo, arquivos, porImagem] = await Promise.all([
        api<ResumoDoLote>(`/analise/lotes/${loteId}`),
        api<ImagemDoLote[]>(`/analise/lotes/${loteId}/imagens`),
        api<ResultadoDeImagem[]>(`/analise/lotes/${loteId}/resultados`),
      ]);
      definirLote(resumo);
      definirImagens(arquivos);
      definirResultados(agrupar(porImagem));
      definirErro(null);
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi
          ? falha
          : new ErroDaApi("Falha inesperada.", 0),
      );
      definirLote(null);
      definirImagens([]);
      definirResultados({});
    } finally {
      definirCarregando(false);
    }
  }, [loteId]);

  useEffect(() => {
    void buscar();
  }, [buscar]);

  // O relógio anda só enquanto o worker tem trabalho neste lote.
  useEffect(() => {
    if (relogio.current) clearTimeout(relogio.current);
    if (!lote || lote.na_fila === 0) return;
    relogio.current = setTimeout(() => void buscar(), INTERVALO_DO_POLLING_MS);
    return () => {
      if (relogio.current) clearTimeout(relogio.current);
    };
  }, [lote, buscar]);

  return { lote, imagens, resultados, carregando, erro, recarregar: buscar };
}
